// ICT-7 Proxy Material Follow Up Report — the same report Alteryx builds,
// pulled step by step so the manual fallback (and the Outstanding Material
// reports) apply one set of rules. Pure: the caller passes jobs, the live
// receipts and the "as of" date (PROXY_SEED_TODAY).
import type { MaterialReceiptRow } from '../types/api';
import { HARD_EXCLUDE_TAGS, type ProxyBand, type ProxyJob } from '../data/mockProxyJobs';
import { resolveOutreachContact } from '../data/proxyFallbackSolicitors';
import { bandFor, daysToMeeting } from './proxyDecisioning';
import { resolveProxyJob, hasOnlySkippedMaterial, type ResolvedProxyJob } from './proxyQueue';
import { OUTREACH_KIND, FOLLOW_UP_INTERVAL_DAYS } from './proxyFollowUpTracker';

export type PullStepId = 'green' | 'duplicate' | 'new' | 'ops_note' | 'recent_follow_up' | 'working_check';

export interface PullStep {
  id: PullStepId;
  label: string;
  sop: string;
  removed: number;
}

export interface ExcludedRow {
  job: ResolvedProxyJob;
  step: PullStepId;
  reason: string;
}

export interface PulledRow {
  job: ResolvedProxyJob;
  band: Exclude<ProxyBand, 'Green'>;
  daysToMeeting: number;
  movedToRed: boolean;   // 15-18 days to meeting, moved from the Yellow tab
  hasContact: boolean;   // found on the Solicitor Contact List or a prior follow-up
}

export interface FollowUpReportPull {
  pulled: number;
  steps: PullStep[];
  excluded: ExcludedRow[];
  rows: PulledRow[];     // kept, Red then Yellow, grouped by solicitor
  movedToRed: number;
}

const STEP_META: Record<PullStepId, { label: string; sop: string }> = {
  green: { label: 'Delete the Green section (more than 30 days to the meeting)', sop: 'ICT-7' },
  duplicate: { label: 'Remove duplicate jobs', sop: 'ICT-7' },
  new: { label: 'Remove jobs whose status is "New"', sop: 'ICT-7' },
  ops_note: { label: 'Remove jobs whose ops note contains an exclusion phrase', sop: 'ICT-7' },
  recent_follow_up: { label: `Remove jobs followed up in the last ${FOLLOW_UP_INTERVAL_DAYS} days`, sop: 'ICT-7' },
  working_check: { label: 'Remove jobs that are not workable (ICT DO NOT FOLLOW UP, e-consent only, only VIF/SMRF outstanding)', sop: 'ICT-1 / ICT-8' },
};

function dayDiff(from: string, to: string): number {
  return Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000);
}

// Whole-phrase match, so "Test" doesn't hit "Contest".
const TAG_PATTERNS = HARD_EXCLUDE_TAGS.map(tag => ({ tag, re: new RegExp(`\\b${tag}\\b`, 'i') }));

export function opsNoteHit(job: ProxyJob): string | null {
  for (const note of job.opsNotes) {
    const hit = TAG_PATTERNS.find(p => p.re.test(note));
    if (hit) return hit.tag;
  }
  return null;
}

function lastOutreach(job: ProxyJob): string | null {
  // ICT-1: a "VIF/BRE only" MIC note keeps the job off the list for 4 days too.
  const dates = job.followUpHistory.filter(r => OUTREACH_KIND[r.type] || r.type === 'vif_bre_only').map(r => r.timestamp.slice(0, 10));
  return dates.length ? dates.sort().slice(-1)[0] : null;
}

function exclusionFor(job: ResolvedProxyJob, seen: Set<string>, today: string): { step: PullStepId; reason: string } | null {
  const toMeeting = daysToMeeting(job);
  if (bandFor(toMeeting) === 'Green') return { step: 'green', reason: `${toMeeting} days to the meeting` };
  if (seen.has(job.jobId)) return { step: 'duplicate', reason: 'Already on the report' };
  if (job.status === 'NEW') return { step: 'new', reason: 'Job status is New' };
  const tag = opsNoteHit(job);
  if (tag) return { step: 'ops_note', reason: `Ops note: ${tag}` };
  const last = lastOutreach(job);
  if (last && dayDiff(last, today) < FOLLOW_UP_INTERVAL_DAYS) return { step: 'recent_follow_up', reason: `Followed up ${last}` };
  if (job.effectiveStatus === 'SUPPRESSED') return { step: 'working_check', reason: 'ICT DO NOT FOLLOW UP' };
  if (job.econsentOnly) return { step: 'working_check', reason: 'All holders are e-consent' };
  if (hasOnlySkippedMaterial(job)) return { step: 'working_check', reason: 'Only VIF/SMRF outstanding' };
  return null;
}

export function pullFollowUpReport(
  jobs: ProxyJob[],
  receipts: MaterialReceiptRow[] | null,
  today: string,
  includeAnyway: Set<string> = new Set(),
): FollowUpReportPull {
  // The Proxy Material Report lists jobs with material still outstanding.
  const outstanding = jobs
    .map(job => resolveProxyJob(job, receipts))
    .filter(job => job.effectiveStatus !== 'RECEIVED' && job.materialStatus !== 'Received' && job.status !== 'ABORTED');

  const seen = new Set<string>();
  const excluded: ExcludedRow[] = [];
  const kept: ResolvedProxyJob[] = [];
  for (const job of outstanding) {
    const hit = exclusionFor(job, seen, today);
    seen.add(job.jobId);
    if (hit && !includeAnyway.has(job.jobId)) excluded.push({ job, ...hit });
    else kept.push(job);
  }

  const rows: PulledRow[] = kept
    .map(job => {
      const toMeeting = daysToMeeting(job);
      return {
        job,
        // An "include anyway" Green job is worked with the Yellow tab.
        band: (bandFor(toMeeting) === 'Red' ? 'Red' : 'Yellow') as PulledRow['band'],
        daysToMeeting: toMeeting,
        movedToRed: toMeeting >= 15 && toMeeting <= 18,
        hasContact: !!resolveOutreachContact(job),
      };
    })
    .sort((a, b) =>
      (a.band === b.band ? 0 : a.band === 'Red' ? -1 : 1)
      || (a.job.solicitor || 'Issuer').localeCompare(b.job.solicitor || 'Issuer')
      || a.daysToMeeting - b.daysToMeeting);

  const steps = (Object.keys(STEP_META) as PullStepId[]).map(id => ({
    id, ...STEP_META[id], removed: excluded.filter(e => e.step === id).length,
  }));

  return { pulled: outstanding.length, steps, excluded, rows, movedToRed: rows.filter(r => r.movedToRed).length };
}
